/*
    What is not in the numbers above, and why (MPN-1688).

    Under the By Campaign table rather than on a tab of its own. A rollup that quietly drops a
    campaign is one somebody will eventually disbelieve, and the answer to "where did
    Northbridge go" belongs on the same screen as the question.

    IT SHOWS THE CONFIDENCE TIER. Excluding a Proven result is a very different act from
    excluding one with insufficient data, and a table that could not show which had happened
    would be one somebody could quietly use to drop results they did not like. That is why
    these rows arrive with the report rather than from the audit ledger alone: the ledger
    knows the reason, only the report knows the tier.

    Restoring is one click and needs no justification. Excluding is the decision that has to
    be explained, and it already is.
*/
import { useState } from "react";

import { excludeRow } from "@services/analytics/holdoutReportService";

import ColumnInfo from "./ColumnInfo";
import { TierPill } from "./StatusPill";
import { EMPTY, pp, pValue, relativeLift, shortDate, type ExcludedRow }
    from "./holdoutReport";

export default function ExcludedRows({ rows, onRestored, canRestore }: {
    rows: ExcludedRow[]; onRestored: () => void;
    /** Restoring a row moves every figure above it. A read-only viewer sees what is missing
        and why -- which is the point of the table -- without the button that puts it back. */
    canRestore: boolean;
}) {
    const [error, setError] = useState<string | null>(null);
    const [restoring, setRestoring] = useState<string | null>(null);

    const restore = async (row: ExcludedRow) => {
        const key = rowKey(row);
        setRestoring(key);
        setError(null);
        try {
            await excludeRow({
                launch_id: row.launch_id,
                // A row excluded as a whole campaign has to be restored as one. Restoring it
                // by its own step would leave the campaign-level exclusion in force and look
                // like the button did nothing.
                journey_step_id: row.whole_campaign ? null : row.journey_step_id,
                is_excluded: false,
            });
            // The row coming back changes every rollup and tile computed from it, so the page
            // refetches rather than this recomputing the pooling a second time.
            onRestored();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not restore that row.");
        } finally {
            setRestoring(null);
        }
    };

    if (rows.length === 0) return null;

    return (
        // The id is the target of the Overview's "Review or restore" link, which is how
        // somebody reading a tile gets to what is missing from it.
        <section className="holdout-report__card mt-4" id="holdout-excluded">
            <div className="holdout-report__section-head">
                <div className="holdout-report__section-title">
                    <h2>Excluded from This Report</h2>
                    <span className="holdout-report__count">{rows.length}</span>
                </div>
                <p className="holdout-report__caption">
                    These rows are left out of every figure above. The campaign data itself is
                    untouched, and restoring one puts it back into the rollups.
                </p>
            </div>
            {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
            <div className="holdout-report__table-wrap">
                <table className="holdout-report__table">
                    <thead>
                        <tr>
                            <th>Client</th><th>Campaign</th><th>Target Action</th>
                            <th className="is-num">Abs. lift<ColumnInfo column="absolute_lift" /></th>
                            <th className="is-num">Rel. lift<ColumnInfo column="relative_lift" /></th>
                            <th className="is-num">P-value<ColumnInfo column="p_value" /></th>
                            <th>Confidence Tier<ColumnInfo column="tier" /></th>
                            <th>Reason</th><th>Detail</th><th>Excluded by</th><th>On</th>
                            {canRestore && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={rowKey(row)}>
                                <td className="is-name">{row.client}</td>
                                <td className="is-name">{row.campaign}</td>
                                <td>
                                    {row.whole_campaign
                                        ? <span className="is-empty">Whole campaign</span>
                                        : row.target_action}
                                </td>
                                <td className="is-num">{pp(row.absolute_lift)}</td>
                                <td className="is-num">
                                    {relativeLift(row.relative_lift, row.control_rate)}
                                </td>
                                <td className="is-num">{pValue(row.p_value)}</td>
                                <td><TierPill tier={row.tier} /></td>
                                <td>{row.reason_label
                                    || <span className="is-empty">{EMPTY}</span>}</td>
                                <td className="is-wrap">{row.detail}</td>
                                <td>{row.excluded_by || "System"}</td>
                                <td className="is-bound">{shortDate(row.excluded_on)}</td>
                                {canRestore && (
                                    <td>
                                        <button type="button"
                                            className="holdout-report__row-action"
                                            disabled={restoring === rowKey(row)}
                                            onClick={() => void restore(row)}>
                                            {restoring === rowKey(row) ? "Restoring..." : "Restore"}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

/** A null step means the whole campaign, which is a different row from any one action. */
function rowKey(row: ExcludedRow): string {
    return `${row.launch_id}-${row.journey_step_id ?? "all"}`;
}
